import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Globe, MapPin, Shield, Clock, Plane, DollarSign, FileText, 
  AlertTriangle, CheckCircle, ArrowLeft, Building2, Calendar,
  Briefcase, Scale
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MarketingHeader from "@/components/layout/MarketingHeader";
import { SEO } from "@/components/seo/SEO";
import { supabase } from "@/integrations/supabase/client";
import { getCountryDisplayName } from "@/utils/countryTranslations";

interface Country {
  id: string;
  name: string;
  tagline: string | null;
  flag_url: string | null;
  image_url: string | null;
  banner_url: string | null;
  risk_level: string | null;
  timezone_utc_offset: string | null;
  tourist_stay_default: string | null;
  remote_work_visa_option: string | null;
  key_requirements: string | null;
  tax_residency_trigger: string | null;
  cost_of_living: number | null;
  attractiveness_rating: number | null;
  ease_compliance: number | null;
  ease_1_5_months: number | null;
  employee_notes: string | null;
  legal_compliance_risks: string | null;
  hr_guidelines: string | null;
  company_caps_observed: string | null;
  renest_notes: string | null;
}

const CountryGuideDetail = () => {
  const { t, i18n } = useTranslation();
  const { country: countrySlug } = useParams();
  const navigate = useNavigate();
  const [country, setCountry] = useState<Country | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (countrySlug) {
      fetchCountry();
    }
  }, [countrySlug]);

  const fetchCountry = async () => {
    try {
      // Convert slug back to country name
      const countryName = countrySlug?.replace(/-/g, ' ');
      
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .ilike('name', countryName || '')
        .single();

      if (error) throw error;
      setCountry(data);
    } catch (error) {
      console.error('Error fetching country:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadge = (riskLevel: string | null) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low':
        return <Badge className="bg-success text-success-foreground">{t("countryGuides.risk.low", "Low Risk")}</Badge>;
      case 'medium':
        return <Badge className="bg-warning text-warning-foreground">{t("countryGuides.risk.medium", "Medium Risk")}</Badge>;
      case 'high':
        return <Badge variant="destructive">{t("countryGuides.risk.high", "High Risk")}</Badge>;
      default:
        return <Badge variant="outline">{t("countryGuides.risk.unknown", "Unknown")}</Badge>;
    }
  };

  const getRatingStars = (rating: number | null) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`h-2 w-6 rounded ${i < rating ? 'bg-primary' : 'bg-muted'}`}
          />
        ))}
        <span className="text-sm text-muted-foreground ml-2">{rating}/5</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MarketingHeader />
        <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/3" />
              <div className="h-4 bg-muted rounded w-2/3" />
              <div className="h-64 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!country) {
    return (
      <div className="min-h-screen bg-background">
        <MarketingHeader />
        <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Globe className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">
              {t("countryGuides.notFound.title", "Country not found")}
            </h1>
            <p className="text-muted-foreground mb-6">
              {t("countryGuides.notFound.description", "The country you're looking for doesn't exist or has been removed.")}
            </p>
            <Button onClick={() => navigate('/country-guides')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("countryGuides.notFound.back", "Back to Country Guides")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const displayName = getCountryDisplayName(country.name, i18n.language);

  return (
    <>
      <SEO 
        title={t("countryGuides.detail.seo.title", "{{country}} Remote Work Guide | Renest", { country: displayName })}
        description={country.tagline || t("countryGuides.detail.seo.description", "Complete guide to remote work from {{country}}. Visa requirements, tax rules, and compliance information.", { country: displayName })}
      />
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        {/* Hero Section */}
        <section className="pt-24 pb-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <Button 
              variant="ghost" 
              className="mb-4"
              onClick={() => navigate('/country-guides')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("countryGuides.detail.backToGuides", "Back to Country Guides")}
            </Button>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  {country.flag_url && (
                    <img 
                      src={country.flag_url} 
                      alt={displayName}
                      className="h-8 w-12 object-cover rounded"
                    />
                  )}
                  <h1 className="text-4xl font-bold">{displayName}</h1>
                </div>
                {country.tagline && (
                  <p className="text-xl text-muted-foreground mb-4">
                    {country.tagline}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mb-6">
                  {getRiskBadge(country.risk_level)}
                  {country.timezone_utc_offset && (
                    <Badge variant="outline">
                      <Clock className="h-3 w-3 mr-1" />
                      {country.timezone_utc_offset}
                    </Badge>
                  )}
                  {country.tourist_stay_default && (
                    <Badge variant="outline">
                      <Plane className="h-3 w-3 mr-1" />
                      {country.tourist_stay_default}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Country Image */}
              <div className="md:w-80 shrink-0">
                <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                  {country.banner_url || country.image_url ? (
                    <img 
                      src={country.banner_url || country.image_url || ''} 
                      alt={displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                      <Globe className="h-16 w-16 text-primary/40" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 border-y bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">
                  {country.ease_compliance ? `${country.ease_compliance}/5` : '-'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("countryGuides.detail.stats.compliance", "Compliance Ease")}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">
                  {country.cost_of_living ? `${country.cost_of_living}/5` : '-'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("countryGuides.detail.stats.costOfLiving", "Cost of Living")}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">
                  {country.attractiveness_rating ? `${country.attractiveness_rating}/5` : '-'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("countryGuides.detail.stats.attractiveness", "Attractiveness")}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary mb-1">
                  {country.ease_1_5_months ? `${country.ease_1_5_months}/5` : '-'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("countryGuides.detail.stats.shortStay", "Short Stay Ease")}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Tabs */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <Tabs defaultValue="overview" className="space-y-8">
              <TabsList className="w-full justify-start overflow-auto">
                <TabsTrigger value="overview">
                  <Globe className="h-4 w-4 mr-2" />
                  {t("countryGuides.detail.tabs.overview", "Overview")}
                </TabsTrigger>
                <TabsTrigger value="visa">
                  <FileText className="h-4 w-4 mr-2" />
                  {t("countryGuides.detail.tabs.visa", "Visa & Entry")}
                </TabsTrigger>
                <TabsTrigger value="tax">
                  <Scale className="h-4 w-4 mr-2" />
                  {t("countryGuides.detail.tabs.tax", "Tax & Compliance")}
                </TabsTrigger>
                <TabsTrigger value="hr">
                  <Briefcase className="h-4 w-4 mr-2" />
                  {t("countryGuides.detail.tabs.hr", "HR Guidelines")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      {t("countryGuides.detail.overview.title", "Country Overview")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {country.employee_notes ? (
                      <p className="text-muted-foreground">{country.employee_notes}</p>
                    ) : (
                      <p className="text-muted-foreground">
                        {t("countryGuides.detail.overview.noInfo", "Detailed information about this destination will be available soon.")}
                      </p>
                    )}
                    
                    {country.renest_notes && (
                      <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          {t("countryGuides.detail.overview.renestTips", "Renest Tips")}
                        </h4>
                        <p className="text-sm text-muted-foreground">{country.renest_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="visa" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plane className="h-5 w-5" />
                      {t("countryGuides.detail.visa.title", "Visa & Entry Requirements")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium mb-2">
                          {t("countryGuides.detail.visa.touristStay", "Tourist Stay Duration")}
                        </h4>
                        <p className="text-muted-foreground">
                          {country.tourist_stay_default || t("countryGuides.detail.notAvailable", "Information not available")}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">
                          {t("countryGuides.detail.visa.remoteWorkVisa", "Remote Work Visa")}
                        </h4>
                        <p className="text-muted-foreground">
                          {country.remote_work_visa_option || t("countryGuides.detail.notAvailable", "Information not available")}
                        </p>
                      </div>
                    </div>

                    {country.key_requirements && (
                      <div>
                        <h4 className="font-medium mb-2">
                          {t("countryGuides.detail.visa.keyRequirements", "Key Requirements")}
                        </h4>
                        <p className="text-muted-foreground">{country.key_requirements}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="tax" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Scale className="h-5 w-5" />
                      {t("countryGuides.detail.tax.title", "Tax & Compliance")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {country.tax_residency_trigger && (
                      <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
                        <h4 className="font-medium mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          {t("countryGuides.detail.tax.residencyTrigger", "Tax Residency Trigger")}
                        </h4>
                        <p className="text-sm text-muted-foreground">{country.tax_residency_trigger}</p>
                      </div>
                    )}

                    {country.legal_compliance_risks && (
                      <div>
                        <h4 className="font-medium mb-2">
                          {t("countryGuides.detail.tax.complianceRisks", "Compliance Risks")}
                        </h4>
                        <p className="text-muted-foreground">{country.legal_compliance_risks}</p>
                      </div>
                    )}

                    {country.company_caps_observed && (
                      <div>
                        <h4 className="font-medium mb-2">
                          {t("countryGuides.detail.tax.companyCaps", "Common Company Policies")}
                        </h4>
                        <p className="text-muted-foreground">{country.company_caps_observed}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="hr" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      {t("countryGuides.detail.hr.title", "HR Guidelines")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {country.hr_guidelines ? (
                      <p className="text-muted-foreground">{country.hr_guidelines}</p>
                    ) : (
                      <p className="text-muted-foreground">
                        {t("countryGuides.detail.hr.noInfo", "HR-specific guidelines for this country will be available soon.")}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 border-t">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">
              {t("countryGuides.detail.cta.title", "Ready to send employees to {{country}}?", { country: displayName })}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t("countryGuides.detail.cta.description", "Our platform handles compliance automatically, so you can focus on your business.")}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button onClick={() => navigate('/company-registration')}>
                {t("countryGuides.detail.cta.getStarted", "Get Started Free")}
              </Button>
              <Button variant="outline" onClick={() => navigate('/contact')}>
                {t("countryGuides.detail.cta.talkToExpert", "Talk to an Expert")}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CountryGuideDetail;
